import { PrivateAxiosInstance } from '@/api/axios'
import { isAxiosError } from 'axios'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

const app = initializeApp(firebaseConfig)

// Get messaging instance - Firebase will auto-register the service worker
let messaging: ReturnType<typeof getMessaging> | null = null

const getMessagingInstance = async () => {
  if (typeof window === 'undefined') return null

  const supported = await isSupported()
  if (!supported) {
    console.log('Firebase Messaging is not supported in this browser')
    return null
  }

  if (!messaging) {
    messaging = getMessaging(app)
  }

  return messaging
}

export { getMessagingInstance }

export const requestForToken = async () => {
  try {
    const messagingInstance = await getMessagingInstance()
    if (!messagingInstance) {
      console.log('Messaging is not available')
      return null
    }

    const currentToken = await getToken(messagingInstance, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    })

    if (currentToken) {
      // Use the same post logic as use-api.ts but outside of React hook context
      try {
        await PrivateAxiosInstance.post(`/fcm-token`, { token: currentToken })
      } catch (error: unknown) {
        let message = 'An error occurred while saving token'

        if (isAxiosError(error)) {
          if (error?.response) {
            message = error?.response?.data?.message || message
            if (error.response.status === 429) {
              message = 'Too many requests. Please try again later.'
            }
          }
        }

        console.error('Failed to save notification token:', message)
        // Don't throw - allow the token to be returned even if saving fails
      }

      return currentToken
    } else {
      console.log('No registration token available. Request permission to generate one.')
    }
  } catch (err) {
    console.log('An error occurred while retrieving token. ', err)
  }
}

export const onMessageListener = async () => {
  const messagingInstance = await getMessagingInstance()
  if (!messagingInstance) {
    console.log('Messaging is not available')
    return Promise.reject(new Error('Messaging is not available'))
  }

  return new Promise((resolve) => {
    onMessage(messagingInstance, (payload) => {
      resolve(payload)
    })
  })
}
